import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Send, MessageCircleQuestion, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { submitBrokerInput } from "@/lib/orcaBackend";

interface Question {
  id: number;
  question: string;
  answer: string | null;
  created_at: string;
}

interface MissingInfoModalProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const MissingInfoModal = ({
  conversationId,
  isOpen,
  onClose,
}: MissingInfoModalProps) => {
  const [brokerInput, setBrokerInput] = useState("");
  const queryClient = useQueryClient();
  const { profile, session } = useAuth();

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["conversation-questions", conversationId],
    queryFn: async () => {
      console.log("Fetching questions for conversation:", conversationId);
      const { data, error } = await supabase
        .from("question_answer")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching questions:", error);
        throw error;
      }

      console.log("Fetched questions:", data);
      return data as Question[];
    },
    enabled: isOpen && !!conversationId,
    refetchInterval: 1000, // Refresh every 1 second
  });

  const brokerInputMutation = useMutation({
    mutationFn: async (input: string) => {
      console.log("Submitting broker input:", {
        conversation_id: conversationId,
        input,
      });

      return submitBrokerInput({
        conversationId,
        input,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });
    },
    onSuccess: () => {
      console.log("Broker input submitted successfully");
      setBrokerInput("");
      queryClient.invalidateQueries({
        queryKey: ["conversation-questions", conversationId],
      });
      // Also invalidate the unanswered questions count to keep it in sync
      queryClient.invalidateQueries({
        queryKey: ["unanswered-questions-counts"],
      });
    },
    onError: (error) => {
      alert(error.message || "Failed to submit broker input.");
    },
  });

  const unansweredQuestions = questions.filter((q) => !q.answer);
  const answeredQuestions = questions.filter((q) => q.answer);

  const handleSubmit = () => {
    if (brokerInput.trim()) {
      brokerInputMutation.mutate(brokerInput.trim());
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-4xl flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <MessageCircleQuestion className="h-5 w-5" />
            <span>Missing Information</span>
            <span className="text-sm text-slate-600">
              ({unansweredQuestions.length} unanswered)
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6">
          {/* Left side - Questions */}
          <div className="flex min-h-[320px] flex-1 flex-col">
            <Tabs defaultValue="unanswered" className="flex-1 flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value="unanswered"
                  className="flex items-center space-x-2"
                >
                  <MessageCircleQuestion className="h-4 w-4" />
                  <span>Unanswered ({unansweredQuestions.length})</span>
                </TabsTrigger>
                <TabsTrigger
                  value="answered"
                  className="flex items-center space-x-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Answered ({answeredQuestions.length})</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="unanswered" className="flex-1 mt-4">
                <ScrollArea className="h-[44dvh] rounded-md border p-4 lg:h-96">
                  {isLoading ? (
                    <div className="text-center py-4">Loading questions...</div>
                  ) : unansweredQuestions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No unanswered questions</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {unansweredQuestions.map((question, index) => (
                        <div
                          key={question.id}
                          className="p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-start space-x-2">
                            <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </span>
                            <p className="text-sm text-slate-700">
                              {question.question}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="answered" className="flex-1 mt-4">
                <ScrollArea className="h-[44dvh] rounded-md border p-4 lg:h-96">
                  {isLoading ? (
                    <div className="text-center py-4">Loading questions...</div>
                  ) : answeredQuestions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <MessageCircleQuestion className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No answered questions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {answeredQuestions.map((question) => (
                        <div
                          key={question.id}
                          className="p-4 bg-green-50 rounded-lg border border-green-200"
                        >
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-800 mb-1">
                                  Q: {question.question}
                                </p>
                                <p className="text-sm text-slate-600">
                                  A: {question.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right side - Input */}
          <div className="flex w-full flex-col space-y-4 lg:w-80">
            <h3 className="font-semibold text-slate-800">
              Provide Information
            </h3>
            <Textarea
              placeholder="Provide information to help answer these questions..."
              value={brokerInput}
              onChange={(e) => setBrokerInput(e.target.value)}
              disabled={brokerInputMutation.isPending}
              className="min-h-32 flex-1 resize-none lg:min-h-[300px]"
            />
            <Button
              onClick={handleSubmit}
              disabled={!brokerInput.trim() || brokerInputMutation.isPending}
              className="w-full"
            >
              {brokerInputMutation.isPending ? (
                "Submitting..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Information
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
